import { buildApiUrl } from '../config/api';
import { TeamMember } from '../types';
import { api } from './api';
import { User, AppRole } from '../types/auth';
import { getVertivEmailVariants } from '../utils/emailNormalization';

/**
 * Team Members Service
 * This service provides team member data by fetching users from the backend API
 * and converting them to the TeamMember format.
 *
 * This service now fetches from the backend API when available and falls back to
 * a static list for development/testing when the API is not available.
 */

// Map AppRoles to TeamMember types
const roleToTypeMap: Record<
  string,
  | 'vertiv_sponsor'
  | 'vertiv_contributor'
  | 'partner_contributor'
  | 'default_sponsor_nam'
  | 'default_sponsor_emea'
  | 'default_sponsor_apac'
  | undefined
> = {
  [AppRole.VERTIV_SPONSOR]: 'vertiv_sponsor',
  [AppRole.VERTIV_CONTRIBUTOR]: 'vertiv_contributor',
  [AppRole.PARTNER_CONTRIBUTOR]: 'partner_contributor',
  [AppRole.DEFAULT_SPONSOR_NAM]: 'default_sponsor_nam',
  [AppRole.DEFAULT_SPONSOR_EMEA]: 'default_sponsor_emea',
  [AppRole.DEFAULT_SPONSOR_APAC]: 'default_sponsor_apac',
};

// Map AppRoles to display roles
const roleToDisplayMap: Record<string, string> = {
  [AppRole.VERTIV_SPONSOR]: 'Sponsor',
  [AppRole.VERTIV_CONTRIBUTOR]: 'Contributor',
  [AppRole.PARTNER_CONTRIBUTOR]: 'Partner',
  [AppRole.DEFAULT_SPONSOR_NAM]: 'Default Sponsor NAM',
  [AppRole.DEFAULT_SPONSOR_EMEA]: 'Default Sponsor EMEA',
  [AppRole.DEFAULT_SPONSOR_APAC]: 'Default Sponsor APAC',
};

/**
 * Convert a User object to a TeamMember object
 */
const userToTeamMember = (user: User): TeamMember | null => {
  // Find the first matching role that maps to a team member type
  const teamRole = user.roles.find(role => roleToTypeMap[role] !== undefined);

  if (!teamRole || !roleToTypeMap[teamRole]) {
    return null; // User doesn't have a team member role
  }

  const type = roleToTypeMap[teamRole];
  const displayRole = roleToDisplayMap[teamRole] || 'Team Member';

  // Create clean name for database storage and displayName for UI
  const namePrefix =
    type === 'vertiv_sponsor'
      ? 'Vertiv Sponsor'
      : type === 'vertiv_contributor'
      ? 'Vertiv Contributor'
      : 'Partner';

  return {
    id: user.id,
    name: user.name, // Clean name for database storage
    email: user.email,
    type: type!,
    role: displayRole,
    displayName: `${namePrefix}/${user.name}`, // Formatted name for UI display
  };
};

/**
 * Fetch all users from the auth service and convert to team members
 * This function fetches from the backend API
 */
const fetchAllTeamMembers = async (): Promise<TeamMember[]> => {
  try {
    const response = await fetch(buildApiUrl('/api/mock-auth/users'), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = (await response.json()) as any;

      if (data.success && data.users) {
        // Convert backend users to User format
        const backendUsers: User[] = data.users.map((user: any) => ({
          id: user.email,
          name: user.name,
          email: user.email,
          phone: '',
          userType: user.userType,
          adGroups: [],
          roles: user.roles,
        }));

        // Convert users to team members, filtering out non-team-member users
        return backendUsers
          .map(userToTeamMember)
          .filter((member): member is TeamMember => member !== null);
      }
    }

    // Return empty array if API is not available or fails
    return [];
  } catch (error) {
    console.error('Failed to fetch team members from API:', error);
    return [];
  }
};

// Map team member types to database role names
const teamTypeToRoleNameMap: Record<
  | 'vertiv_sponsor'
  | 'vertiv_contributor'
  | 'partner_contributor'
  | 'default_sponsor_nam'
  | 'default_sponsor_emea'
  | 'default_sponsor_apac',
  string
> = {
  vertiv_sponsor: 'Vertiv Sponsor',
  vertiv_contributor: 'Vertiv Contributor',
  partner_contributor: 'Partner Contributor',
  default_sponsor_nam: 'Default Sponsor NAM',
  default_sponsor_emea: 'Default Sponsor EMEA',
  default_sponsor_apac: 'Default Sponsor APAC',
};

class TeamMembersService {
  private cachedTeamMembers: TeamMember[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 30000; // 30 seconds cache (increased from 5s)
  private roleCache: Map<string, number> = new Map(); // Cache role name -> role ID mappings

  // Request-level cache to prevent duplicate parallel requests
  private roleRequestCache: Map<string, Promise<TeamMember[]>> = new Map();
  private roleCacheData: Map<
    string,
    { data: TeamMember[]; timestamp: number }
  > = new Map();

  /**
   * Get role ID by name from the role-management API
   */
  private async getRoleIdByName(roleName: string): Promise<number | undefined> {
    try {
      const data = await api.get<{
        roles?: Array<{ id: number; name: string }>;
      }>('/role-management/roles');

      const role = data.roles?.find((r: any) => r.name === roleName);

      if (role) {
        this.roleCache.set(roleName, role.id);
        return role.id;
      }

      console.warn(
        `⚠️ getRoleIdByName: Role "${roleName}" not found in API response`,
      );
      return undefined;
    } catch (error) {
      console.error(`❌ getRoleIdByName: Exception caught:`, error);
      return undefined;
    }
  }

  /**
   * Fetch users by role from role-management API
   * Uses request-level caching to prevent duplicate parallel requests
   */
  async getUsersByRole(
    type:
      | 'vertiv_sponsor'
      | 'vertiv_contributor'
      | 'partner_contributor'
      | 'default_sponsor_nam'
      | 'default_sponsor_emea'
      | 'default_sponsor_apac',
  ): Promise<TeamMember[]> {
    const roleName = teamTypeToRoleNameMap[type];

    // Partner Contributor results are caller-scoped. A role-only cache would
    // reuse an internal unscoped list after impersonation or user switch.
    const skipViewerScopedCache = type === 'partner_contributor';

    if (!skipViewerScopedCache) {
      const now = Date.now();
      const cachedData = this.roleCacheData.get(type);
      if (cachedData && now - cachedData.timestamp < this.CACHE_DURATION) {
        return cachedData.data;
      }

      const existingRequest = this.roleRequestCache.get(type);
      if (existingRequest) {
        return existingRequest;
      }
    }

    const requestPromise = (async () => {
      try {
        // Check cache first
        let roleId = this.roleCache.get(roleName);

        // Fetch role ID if not cached
        if (!roleId) {
          roleId = await this.getRoleIdByName(roleName);
          if (!roleId) {
            console.warn(
              `⚠️ getUsersByRole: Could not find role ID for ${roleName}`,
            );
            return [];
          }
        }

        const data = await api.get<{
          users?: Array<{
            user_id: number;
            upn?: string;
            email: string;
            last_login?: string;
          }>;
        }>(`/role-management/roles/${roleId}/team-members?limit=500`);

        // Transform users to TeamMember format
        // Note: Converting null to undefined for optional fields
        // Database returns null for missing values, but TeamMember interface uses optional properties (undefined)
        const teamMembers: TeamMember[] =
          data.users?.map((user: any) => {
            const firstName = user.first_name || '';
            const lastName = user.last_name || '';
            const fullName =
              [firstName, lastName].filter(Boolean).join(' ') ||
              user.upn ||
              user.email;

            return {
              id: user.user_id.toString(),
              firstName: firstName || undefined, // Convert empty string/null to undefined for optional field
              lastName: lastName || undefined, // Convert empty string/null to undefined for optional field
              name: fullName,
              email: user.email,
              type,
              role: teamTypeToRoleNameMap[type],
              displayName: fullName,
            };
          }) || [];

        if (!skipViewerScopedCache) {
          this.roleCacheData.set(type, {
            data: teamMembers,
            timestamp: Date.now(),
          });
        }

        return teamMembers;
      } catch (error) {
        console.error(`❌ getUsersByRole: Error fetching from API:`, error);
        return [];
      } finally {
        // Remove from request cache when done
        this.roleRequestCache.delete(type);
      }
    })();

    if (!skipViewerScopedCache) {
      this.roleRequestCache.set(type, requestPromise);
    }

    return requestPromise;
  }

  /**
   * Get all team members
   * Fetches from auth service and caches the result
   */
  async getAllTeamMembers(): Promise<TeamMember[]> {
    const now = Date.now();

    // Return cached data if still valid
    if (
      this.cachedTeamMembers &&
      now - this.cacheTimestamp < this.CACHE_DURATION
    ) {
      return this.cachedTeamMembers;
    }

    // Fetch fresh data
    const teamMembers = await fetchAllTeamMembers();

    // Update cache
    this.cachedTeamMembers = teamMembers;
    this.cacheTimestamp = now;

    return teamMembers;
  }

  /**
   * Get team members by type
   * Filters the full list by the specified type
   */
  async getTeamMembersByType(
    type: 'vertiv_sponsor' | 'vertiv_contributor' | 'partner_contributor',
  ): Promise<TeamMember[]> {
    // Clear cache to ensure fresh data for partner contributors
    if (type === 'partner_contributor') {
      this.clearCache();
    }

    const allMembers = await this.getAllTeamMembers();
    const filtered = allMembers.filter(member => member.type === type);

    return filtered;
  }

  /**
   * Search team members
   * Searches both name and email fields
   */
  async searchTeamMembers(
    query: string,
    type?: 'vertiv_sponsor' | 'vertiv_contributor' | 'partner_contributor',
  ): Promise<TeamMember[]> {
    const allMembers = await this.getAllTeamMembers();
    let results = allMembers;

    // Filter by type if provided
    if (type) {
      results = results.filter(member => member.type === type);
    }

    // Search by query in name and email
    if (query) {
      const lowerQuery = query.toLowerCase();
      results = results.filter(
        member =>
          member.name.toLowerCase().includes(lowerQuery) ||
          member.email.toLowerCase().includes(lowerQuery),
      );
    }

    return results;
  }

  /**
   * Get team members by IDs
   * Returns team members matching the provided IDs
   * Fetches from role-based endpoints to get database users
   */
  async getTeamMembersByIds(ids: string[]): Promise<TeamMember[]> {
    try {
      // Fetch all team member types from database (including Default Sponsor roles)
      const [
        sponsors,
        contributors,
        partners,
        defaultSponsorsNAM,
        defaultSponsorsEMEA,
        defaultSponsorsAPAC,
      ] = await Promise.all([
        this.getUsersByRole('vertiv_sponsor'),
        this.getUsersByRole('vertiv_contributor'),
        this.getUsersByRole('partner_contributor'),
        this.getUsersByRole('default_sponsor_nam'),
        this.getUsersByRole('default_sponsor_emea'),
        this.getUsersByRole('default_sponsor_apac'),
      ]);

      // Combine all team members and deduplicate by email
      const allMembersArray = [
        ...sponsors,
        ...contributors,
        ...partners,
        ...defaultSponsorsNAM,
        ...defaultSponsorsEMEA,
        ...defaultSponsorsAPAC,
      ];
      const uniqueMembersMap = new Map<string, TeamMember>();
      for (const member of allMembersArray) {
        if (!uniqueMembersMap.has(member.email)) {
          uniqueMembersMap.set(member.email, member);
        }
      }
      const allMembers = Array.from(uniqueMembersMap.values());

      // Filter by requested IDs
      return allMembers.filter(member => ids.includes(member.id));
    } catch (error) {
      // Fallback to legacy method
      const allMembers = await this.getAllTeamMembers();
      return allMembers.filter(member => ids.includes(member.id));
    }
  }

  /**
   * Get team members by emails
   * Returns team members matching the provided email addresses
   * Fetches from role-based endpoints to get database users
   */
  async getTeamMembersByEmails(emails: string[]): Promise<TeamMember[]> {
    try {
      // Fetch all team member types from database (including Default Sponsor roles)
      const [
        sponsors,
        contributors,
        partners,
        defaultSponsorsNAM,
        defaultSponsorsEMEA,
        defaultSponsorsAPAC,
      ] = await Promise.all([
        this.getUsersByRole('vertiv_sponsor'),
        this.getUsersByRole('vertiv_contributor'),
        this.getUsersByRole('partner_contributor'),
        this.getUsersByRole('default_sponsor_nam'),
        this.getUsersByRole('default_sponsor_emea'),
        this.getUsersByRole('default_sponsor_apac'),
      ]);

      // Combine all team members and deduplicate by email
      const allMembersArray = [
        ...sponsors,
        ...contributors,
        ...partners,
        ...defaultSponsorsNAM,
        ...defaultSponsorsEMEA,
        ...defaultSponsorsAPAC,
      ];
      const uniqueMembersMap = new Map<string, TeamMember>();
      for (const member of allMembersArray) {
        if (!uniqueMembersMap.has(member.email)) {
          uniqueMembersMap.set(member.email, member);
        }
      }
      const allMembers = Array.from(uniqueMembersMap.values());

      // Filter by requested emails (variant-aware for @vertiv.com/@vertivco.com)
      const allVariants = emails.flatMap(e => getVertivEmailVariants(e));
      return allMembers.filter(member =>
        allVariants.includes(member.email.toLowerCase()),
      );
    } catch (error) {
      // Fallback to legacy method (variant-aware)
      const allMembers = await this.getAllTeamMembers();
      const allVariants = emails.flatMap(e => getVertivEmailVariants(e));
      return allMembers.filter(member =>
        allVariants.includes(member.email.toLowerCase()),
      );
    }
  }

  /**
   * Get a single team member by email
   * Returns the team member matching the provided email address
   * Fetches from role-based endpoints to get database users
   */
  async getTeamMemberByEmail(email: string): Promise<TeamMember | null> {
    try {
      // Fetch all team member types from database (including Default Sponsor roles)
      const [
        sponsors,
        contributors,
        partners,
        defaultSponsorsNAM,
        defaultSponsorsEMEA,
        defaultSponsorsAPAC,
      ] = await Promise.all([
        this.getUsersByRole('vertiv_sponsor'),
        this.getUsersByRole('vertiv_contributor'),
        this.getUsersByRole('partner_contributor'),
        this.getUsersByRole('default_sponsor_nam'),
        this.getUsersByRole('default_sponsor_emea'),
        this.getUsersByRole('default_sponsor_apac'),
      ]);

      // Combine all team members and deduplicate by email
      const allMembersArray = [
        ...sponsors,
        ...contributors,
        ...partners,
        ...defaultSponsorsNAM,
        ...defaultSponsorsEMEA,
        ...defaultSponsorsAPAC,
      ];
      const uniqueMembersMap = new Map<string, TeamMember>();
      for (const member of allMembersArray) {
        if (!uniqueMembersMap.has(member.email)) {
          uniqueMembersMap.set(member.email, member);
        }
      }
      const allMembers = Array.from(uniqueMembersMap.values());

      // Find by email (variant-aware for @vertiv.com/@vertivco.com)
      const variants = getVertivEmailVariants(email);
      return (
        allMembers.find(member =>
          variants.includes(member.email.toLowerCase()),
        ) || null
      );
    } catch (error) {
      // Fallback to legacy method (variant-aware)
      const allMembers = await this.getAllTeamMembers();
      const variants = getVertivEmailVariants(email);
      return (
        allMembers.find(member =>
          variants.includes(member.email.toLowerCase()),
        ) || null
      );
    }
  }

  /**
   * Get Default Sponsors by region
   * Returns Default Sponsors for the specified region (NAM, EMEA, or APAC)
   * @throws {Error} If region is invalid or not provided
   */
  async getDefaultSponsorsByRegion(region: string): Promise<TeamMember[]> {
    // Define valid regions constant
    const VALID_REGIONS = ['nam', 'emea', 'apac'] as const;

    // Handle null/undefined/empty region
    if (!region || typeof region !== 'string') {
      const errorMsg = `Region is required and must be a string. Received: ${typeof region}`;
      console.error(`❌ getDefaultSponsorsByRegion: ${errorMsg}`);
      throw new Error(errorMsg);
    }

    const normalizedRegion = region.toLowerCase().trim();

    // Validate region against allowed values
    if (!VALID_REGIONS.includes(normalizedRegion as any)) {
      const errorMsg = `Invalid region "${region}". Must be one of: ${VALID_REGIONS.map(
        r => r.toUpperCase(),
      ).join(', ')}`;
      console.error(`❌ getDefaultSponsorsByRegion: ${errorMsg}`);
      throw new Error(errorMsg);
    }

    // Map region to Default Sponsor type
    const regionToTypeMap: Record<
      string,
      'default_sponsor_nam' | 'default_sponsor_emea' | 'default_sponsor_apac'
    > = {
      nam: 'default_sponsor_nam',
      emea: 'default_sponsor_emea',
      apac: 'default_sponsor_apac',
    };

    const type = regionToTypeMap[normalizedRegion];

    try {
      const defaultSponsors = await this.getUsersByRole(type);
      return defaultSponsors;
    } catch (error) {
      console.error(
        `❌ getDefaultSponsorsByRegion: Error fetching Default Sponsors for ${region}:`,
        error,
      );
      throw error; // Re-throw to allow caller to handle
    }
  }

  /**
   * Clear the cache - useful when team members are updated
   */
  clearCache(): void {
    this.cachedTeamMembers = null;
    this.cacheTimestamp = 0;
    this.roleCache.clear();
    this.roleCacheData.clear();
    this.roleRequestCache.clear();
  }
}

export const teamMembersService = new TeamMembersService();
